import type * as React from "react"
import { useEffect, useState } from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { paiseToInputValue, parseRupeesToPaise } from "@/lib/money"

/**
 * One end of an amount range: the owner types RUPEES, the API takes PAISE.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SHARED BY ALL THREE REGISTERS, and that is the whole reason it lives here
 * rather than inside the bill sheet where it started. The invoice, quotation and
 * challan filter sheets each need "at least / at most", and three copies of a
 * money input is three chances to disagree about what an empty box means, about
 * whether the value commits per keystroke or on blur, and — the expensive one —
 * about whether rupees become paise by splitting the string or by multiplying a
 * float. `components/DateField` and `lib/date-presets` are here for the same
 * reason: the three sheets already agree through shared code, not through three
 * people remembering.
 *
 * THE CONVERSION IS `parseRupeesToPaise`, NEVER `parseFloat(x) * 100`. Money is
 * integer paise everywhere (CLAUDE.md); `parseRupeesToPaise` splits the typed
 * string on the decimal point and reads the two halves as integers, so it cannot
 * drift. It also accepts what a person actually types — `1,45,600.50`, `₹6750`,
 * a stray space — which is why the field is not restricted to digits.
 *
 * IT COMMITS ON BLUR, NOT PER KEYSTROKE. The value lands in the URL, and the URL
 * is the filter state on all three registers; rewriting it on every character
 * would fire a request per keystroke and stack history entries between the owner
 * and the screen he came from. The `useEffect` re-syncs the text when the value
 * changes from ELSEWHERE — a cleared chip, the back button — without fighting the
 * user mid-keystroke.
 *
 * AN EMPTY BOX IS "NO FILTER", NOT ZERO. Emptying it sends `undefined`, which
 * deletes the parameter; sending `0` would be a real bound that quietly excludes
 * nothing on `min` and EVERYTHING on `max`. Unreadable input clears the bound for
 * the same reason — `NaN` on the wire earns a 400 the owner cannot read.
 *
 * NO GROUPING WHILE EDITING (`paiseToInputValue` emits `67.50`, not `₹67.50`): a
 * comma inserted into a field mid-edit moves the caret, which is the classic way
 * an amount gets typed twice. Grouping is for DISPLAY — the chip formats through
 * `formatRupees`.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function RupeeBound({
  id,
  label,
  paise,
  onChange,
}: {
  /** Ties the label to the input — without it the box is unreachable by name. */
  id: string
  label: string
  paise: number | undefined
  onChange: (paise: number | undefined) => void
}): React.JSX.Element {
  const [text, setText] = useState(paise === undefined ? "" : paiseToInputValue(paise))

  // Follow the URL when it changes from elsewhere — a cleared filter, the back
  // button — without fighting the user mid-keystroke.
  useEffect(() => {
    setText(paise === undefined ? "" : paiseToInputValue(paise))
  }, [paise])

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-muted-foreground text-xs">
        {label}
      </Label>
      <Input
        id={id}
        inputMode="decimal"
        className="tabular-nums"
        placeholder="0.00"
        value={text}
        onChange={(event) => {
          setText(event.target.value)
        }}
        onBlur={() => {
          const trimmed = text.trim()
          if (trimmed === "") {
            onChange(undefined)
            return
          }
          const parsed = parseRupeesToPaise(trimmed)
          // Unreadable input clears the bound rather than sending NaN, which the
          // schema would reject with a message about paise the owner never saw.
          onChange(parsed ?? undefined)
        }}
      />
    </div>
  )
}
